import React, { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { t } from 'i18next';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import moment from 'moment';
import normalizeEventDate from 'utils/normalizeEventDate';
import getEventStatus, { isEventCancelled } from 'utils/eventStatus';
import { getEventDateRange, getTodayDateOnly } from 'utils/eventDateRange';
import {
  Menu,
  MenuButton,
  MenuItems,
  MenuItem,
  Transition,
} from '@headlessui/react';
import ProgressBar from 'components/ProgressBar';
import CategoryList from 'components/CategoryList';
import {
  EllipsisHorizontalIcon,
  CogIcon,
  EyeIcon,
  BuildingStorefrontIcon,
  NoSymbolIcon,
  MapPinIcon,
} from '@heroicons/react/24/outline';
import ScaleBox from 'components/ScaleBox';
import { cancelBooking, getBrandEvents } from '../../redux/actions/bookings';
import { canCancelBooking } from 'utils/bookingActionability';
import { useAlert } from 'context/AlertContext';

const EventStatusBadge = ({ event }) => {
  const { t } = useTranslation();
  const cancelled = isEventCancelled(event);
  const status = getEventStatus(event?.date);
  if (!cancelled && status !== 'live' && status !== 'finished') return null;

  const isLive = !cancelled && status === 'live';
  let label;
  if (cancelled) label = t('listing.event_status_cancelled') || 'Cancelled';
  else if (isLive) label = t('listing.event_status_live') || 'Live';
  else label = t('listing.event_status_finished') || 'Finished';
  let palette;
  if (cancelled) {
    palette = {
      border: 'border-rose-400',
      text: 'text-rose-600',
      dot: 'bg-rose-500',
    };
  } else if (isLive) {
    palette = {
      border: 'border-green-500',
      text: 'text-green-600',
      dot: 'bg-green-500',
    };
  } else {
    palette = {
      border: 'border-gray-400',
      text: 'text-gray-500',
      dot: 'bg-gray-400',
    };
  }

  return (
    <div
      className={`absolute -top-2 left-1/2 -translate-x-1/2 z-30 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white border ${palette.border} ${palette.text} text-[9px] sm:text-[10px] font-bold uppercase tracking-normal leading-none shadow-sm whitespace-nowrap`}
    >
      <span
        className={`w-1.5 h-1.5 rounded-full ${palette.dot} ${isLive ? 'animate-pulse' : ''}`}
      />
      <span>{label}</span>
    </div>
  );
};

const DummyRibbon = ({ label = t('listing.example') }) => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none z-10 rounded-2xl sm:rounded-3xl">
    <div
      className="absolute bg-amber-500 text-white text-[11px] font-bold uppercase tracking-widest text-center leading-[26px] h-[26px]"
      style={{
        top: '32px',
        right: '-38px',
        width: '170px',
        transform: 'rotate(45deg)',
        transformOrigin: 'center',
        boxShadow: '0 2px 6px rgba(0,0,0,.18)',
      }}
    >
      {label}
    </div>
  </div>
);

const ListingDummyRibbon = ({ label = t('listing.example') }) => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none z-20 rounded-3xl">
    <div
      className="absolute bg-amber-500 text-white text-[11px] font-bold uppercase tracking-widest text-center leading-[26px] h-[26px]"
      style={{
        top: '32px',
        right: '-38px',
        width: '170px',
        transform: 'rotate(45deg)',
        boxShadow: '0 2px 6px rgba(0,0,0,.18)',
      }}
    >
      {label}
    </div>
  </div>
);

const stripDotsListing = (s) =>
  typeof s === 'string' ? s.replaceAll('.', '') : s;

// "15 Nov 2026". Month names are capitalised by hand because some locales
// (pt) spell them in lowercase.
const fmtDMMMYYYYListing = (d, locale) => {
  const m = moment(d);
  if (!m.isValid()) return '';
  const month = stripDotsListing(m.clone().locale(locale).format('MMM'));
  return `${m.date()} ${month.charAt(0).toUpperCase()}${month.slice(1)} ${m.year()}`;
};

const getListingDateInfo = (rawDate, locale) => {
  const date = normalizeEventDate(rawDate);
  const range = getEventDateRange(date);
  if (!range) return null;
  const mStart = moment(range.startDate);
  const mEnd = moment(range.endDate);
  // The pill shows the first day only; the days pill beside it gives the span.
  const label = fmtDMMMYYYYListing(mStart, locale);
  let daysCount;
  if (date?.type === 'segmented' && Array.isArray(date.days)) {
    daysCount = date.days.filter((d) => moment(d?.date).isValid()).length;
  } else {
    daysCount = Math.max(mEnd.diff(mStart, 'days') + 1, 1);
  }
  return { label, daysCount };
};

const ListingDatePill = ({ date, locale, hideDaysPill, compact = false }) => {
  const { t } = useTranslation();
  const info = getListingDateInfo(date, locale);
  if (!info) return null;
  return (
    <div className={`inline-flex items-center ${compact ? 'gap-1' : 'gap-1.5'}`}>
      <span
        className={`inline-flex items-center rounded-full bg-white ring-1 ring-line text-gray-600 font-bold tracking-wide whitespace-nowrap shadow-sm ${
          compact ? 'px-2 py-0.5 text-[10px]' : 'px-2.5 py-1 text-[11px]'
        }`}
      >
        {info.label}
      </span>
      {!hideDaysPill && Number.isFinite(info.daysCount) && info.daysCount > 0 && (
        <span
          className={`inline-flex items-center rounded-full bg-ink-strong text-white font-semibold whitespace-nowrap shadow-sm ${
            compact ? 'px-1.5 py-0.5 text-[10px]' : 'px-2 py-1 text-[10.5px]'
          }`}
        >
          {info.daysCount}{' '}
          {info.daysCount === 1 ? t('common.day') : t('common.days')}
        </span>
      )}
    </div>
  );
};

const ListingStateBadge = ({ event }) => {
  const { t } = useTranslation();
  // A cancelled event is reported as cancelled whatever its dates say, and
  // even when those dates are missing/unparseable (T2B-420).
  if (isEventCancelled(event)) {
    return (
      <span className="inline-flex items-center px-2 py-1 rounded-full bg-rose-50 text-rose-700 ring-1 ring-rose-200 text-[10px] font-bold uppercase tracking-[0.08em]">
        {t('listing.event_status_cancelled')}
      </span>
    );
  }

  const status = getEventStatus(event?.date);
  if (!status) return null;

  if (status === 'live' || event?.state === 'live') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200 text-[10px] font-bold uppercase tracking-[0.08em]">
        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
        {t('listing.event_status_live')}
      </span>
    );
  }

  if (status === 'finished') {
    return (
      <span className="inline-flex items-center px-2 py-1 rounded-full bg-paper text-gray-500 ring-1 ring-line text-[10px] font-bold uppercase tracking-[0.08em]">
        {t('listing.event_status_past')}
      </span>
    );
  }

  const range = getEventDateRange(event?.date);
  if (!range) return null;
  const today = getTodayDateOnly();
  const days = moment(range.startDate, 'YYYY-MM-DD').diff(
    moment(today, 'YYYY-MM-DD'),
    'days'
  );
  if (days > 0 && days <= 14) {
    return (
      <span className="inline-flex items-center px-2 py-1 rounded-full bg-brand-soft text-amber-900 ring-1 ring-amber-200 text-[10px] font-bold uppercase tracking-[0.08em]">
        {t('listing.event_status_in_days', { count: days })}
      </span>
    );
  }
  return null;
};

const ListingFillBar = ({ current, total, compact = false }) => {
  const { t } = useTranslation();
  const safeCurrent = Number(current) || 0;
  const safeTotal = Number(total) || 0;
  const pct =
    safeTotal > 0 ? Math.min((safeCurrent / safeTotal) * 100, 100) : 0;

  let label;
  let bar;
  let labelColor;
  if (safeTotal === 0 || safeCurrent === 0) {
    label = t('listing.fill_spaces_open');
    bar = 'bg-emerald-500';
    labelColor = 'text-emerald-700';
  } else if (pct >= 100) {
    label = t('listing.fill_sold_out');
    bar = 'bg-rose-500';
    labelColor = 'text-rose-600';
  } else if (pct >= 80) {
    label = t('listing.fill_almost_full');
    bar = 'bg-amber-500';
    labelColor = 'text-amber-700';
  } else if (pct > 50) {
    label = t('listing.fill_filling_up');
    bar = 'bg-amber-500';
    labelColor = 'text-amber-700';
  } else {
    label = t('listing.fill_spaces_open');
    bar = 'bg-emerald-500';
    labelColor = 'text-emerald-700';
  }

  return (
    <div className="w-full">
      <div className="flex items-center justify-between mb-1">
        <span
          className={`uppercase font-bold ${
            compact
              ? 'text-[9px] tracking-[0.04em] truncate min-w-0 mr-2'
              : 'text-[10px] tracking-[0.1em]'
          } ${labelColor}`}
        >
          {label}
        </span>
        {safeTotal > 0 && (
          <span className="text-[11px] font-bold tabular-nums text-gray-700 shrink-0">
            {safeCurrent}
            <span className="text-gray-400 font-medium">/{safeTotal}</span>
          </span>
        )}
      </div>
      <div className="w-full h-1 rounded-full overflow-hidden bg-paper ring-1 ring-line">
        <div
          className={`h-full rounded-full transition-all ${bar}`}
          style={{ width: `${Math.max(pct, safeCurrent > 0 ? 4 : 0)}%` }}
        />
      </div>
    </div>
  );
};

const ListingPriceChip = ({ price }) => {
  const { t } = useTranslation();
  if (price == null) return null;
  return (
    <div className="inline-flex items-baseline gap-1 rounded-full px-3 py-1 text-[12px] bg-brand-soft text-ink-strong ring-1 ring-amber-200 font-bold tabular-nums">
      <span className="text-[9px] uppercase tracking-[0.12em] font-bold opacity-70 -translate-y-px">
        {t('common.from_price')}
      </span>
      <span>{price}€</span>
    </div>
  );
};

const ListingEventCard = ({
  event,
  isAuthenticated,
  hideDaysPill,
  smallLogo,
  equalTitleHeight,
}) => {
  const { i18n } = useTranslation();
  const locale = i18n?.language || 'en';
  // Live, past and cancelled events don't advertise availability: bookings
  // are closed once the event is running, over or called off, so the fill bar
  // would only be noise (T2B-397).
  const dateStatus = getEventStatus(event?.date);
  const isLive = dateStatus === 'live' || event?.state === 'live';
  const hideFillBar =
    isLive || dateStatus === 'finished' || isEventCancelled(event);
  // Live events drop the price as well: the door is already closed, so a
  // "from X€" chip advertises a booking that can no longer be made (T2B-412).
  const hidePrice = isLive;
  // The footer (and the divider above it) only shows when it has availability
  // or a price to show, so signed-out visitors don't get an empty ruled line.
  const showFooter = isAuthenticated && (!hideFillBar || !hidePrice);
  const locationLabel =
    event?.city && event?.country
      ? `${event.city}, ${event.country}`
      : event?.city || event?.country || event?.location || '';

  return (
    <div className="relative bg-white rounded-3xl ring-1 ring-line shadow-card-listing hover:shadow-lg transition flex flex-col overflow-hidden h-full">
      {event.isDummy && <ListingDummyRibbon />}

      {/* Cover */}
      <div className="relative aspect-[4/3] lg:aspect-[3/2] bg-paper">
        <div className="absolute inset-0 overflow-hidden">
          {event?.landscapeImage || event?.avatar ? (
            <img
              src={event.landscapeImage || event.avatar}
              alt={event.name || 'Event'}
              className="absolute inset-0 w-full h-full object-cover"
              loading="lazy"
              decoding="async"
            />
          ) : (
            <div className="absolute inset-0 grid place-items-center">
              <BuildingStorefrontIcon className="w-16 h-16 text-gray-300" />
            </div>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/0 to-black/15" />
        </div>

        <div className="absolute top-3 left-3 z-20">
          <ListingStateBadge event={event} />
        </div>

        <div className="absolute left-3 bottom-3 z-20">
          <ListingDatePill
            date={event.date}
            locale={locale}
            hideDaysPill={hideDaysPill}
          />
        </div>

        {/* Logo/avatar badge — straddles the cover/body boundary */}
        <div
          className={`absolute right-4 -bottom-3 z-30 ${
            smallLogo ? 'w-20 h-20' : 'w-28 h-28'
          } rounded-2xl bg-white ring-1 ring-line shadow-md overflow-hidden`}
        >
          {event?.avatar ? (
            <img
              src={event.avatar}
              alt={event.name ? `${event.name} logo` : 'Logo'}
              className="w-full h-full object-cover"
              loading="lazy"
              decoding="async"
            />
          ) : (
            <div className="w-full h-full grid place-items-center">
              <BuildingStorefrontIcon className="w-8 h-8 text-gray-300" />
            </div>
          )}
        </div>
      </div>

      {/* Body */}
      <div className="p-4 flex flex-col gap-3 flex-1">
        <div>
          <h3
            className={`font-extrabold text-[17px] text-ink-strong leading-snug line-clamp-2 break-words tracking-tight ${
              equalTitleHeight ? 'min-h-[2.75em]' : ''
            }`}
          >
            {event.name}
          </h3>
          {locationLabel && (
            <p className="mt-1 text-[12.5px] text-gray-500 flex items-center gap-1">
              <MapPinIcon className="w-3 h-3 shrink-0" />
              <span className="truncate">{locationLabel}</span>
            </p>
          )}
        </div>

        <div className="flex items-center gap-2 min-h-[24px]">
          <CategoryList
            categories={event.categoriesAllowed}
            onlyMacro
            withLabel
            macroIconClass="w-6 h-6"
            containerClass="flex flex-row flex-wrap items-center gap-x-2 gap-y-1 overflow-visible"
          />
        </div>

        {showFooter && (
          <div className="mt-auto pt-2 border-t border-line/70 flex items-end justify-between gap-3">
            <div className="flex-1 min-w-0">
              {!hideFillBar && (
                <ListingFillBar
                  current={event.bookedSpaces}
                  total={event.totalSpaces}
                />
              )}
            </div>
            {!hidePrice && <ListingPriceChip price={event.lowestPrice} />}
          </div>
        )}
      </div>
    </div>
  );
};

// Phone variant of the status badge: upcoming events within a month read as
// plain text ("In 13 days") under the location; live, past and cancelled keep
// their pill.
const ListingStartsIn = ({ event }) => {
  const { t } = useTranslation();
  const status = getEventStatus(event?.date);
  if (
    isEventCancelled(event) ||
    status === 'live' ||
    status === 'finished' ||
    event?.state === 'live'
  ) {
    return <ListingStateBadge event={event} />;
  }
  const range = getEventDateRange(event?.date);
  if (!range) return null;
  const days = moment(range.startDate, 'YYYY-MM-DD').diff(
    moment(getTodayDateOnly(), 'YYYY-MM-DD'),
    'days'
  );
  if (days <= 0 || days > 30) return null;
  return (
    <span className="text-[12px] font-semibold text-amber-700">
      {t('listing.starts_in_days', { count: days })}
    </span>
  );
};

// Two-up grid card for the events listing on phones: short cover with the
// date on it, round logo overlapping the cover, and the categories beside it.
const ListingCompactCard = ({ event, isAuthenticated }) => {
  const { t, i18n } = useTranslation();
  const locale = i18n?.language || 'en';
  const dateStatus = getEventStatus(event?.date);
  const isLive = dateStatus === 'live' || event?.state === 'live';
  const hideFillBar =
    isLive || dateStatus === 'finished' || isEventCancelled(event);
  const hidePrice = isLive || event?.lowestPrice == null;
  const showFooter = isAuthenticated && (!hideFillBar || !hidePrice);
  const locationLabel =
    event?.city && event?.country
      ? `${event.city}, ${event.country}`
      : event?.city || event?.country || event?.location || '';

  return (
    <div className="relative bg-white rounded-2xl ring-1 ring-line shadow-card-listing flex flex-col overflow-hidden h-full">
      {event.isDummy && <ListingDummyRibbon />}

      {/* Cover */}
      <div className="relative aspect-[16/9] bg-paper">
        <div className="absolute inset-0 overflow-hidden">
          {event?.landscapeImage || event?.avatar ? (
            <img
              src={event.landscapeImage || event.avatar}
              alt={event.name || 'Event'}
              className="absolute inset-0 w-full h-full object-cover"
              loading="lazy"
              decoding="async"
            />
          ) : (
            <div className="absolute inset-0 grid place-items-center">
              <BuildingStorefrontIcon className="w-10 h-10 text-gray-300" />
            </div>
          )}
        </div>
        <div className="absolute top-2 left-2 right-2 z-20">
          <ListingDatePill date={event.date} locale={locale} compact />
        </div>
      </div>

      {/* Logo overlapping the cover + categories */}
      <div className="-mt-6 px-2.5 flex items-end gap-1.5 relative z-10">
        <div className="w-12 h-12 rounded-full overflow-hidden ring-[3px] ring-white bg-white shadow-card shrink-0">
          {event?.avatar ? (
            <img
              src={event.avatar}
              alt={event.name ? `${event.name} logo` : 'Logo'}
              className="w-full h-full object-cover"
              loading="lazy"
              decoding="async"
            />
          ) : (
            <div className="w-full h-full grid place-items-center bg-paper">
              <BuildingStorefrontIcon className="w-6 h-6 text-gray-300" />
            </div>
          )}
        </div>
        <div className="flex-1 min-w-0 flex justify-end pb-0.5">
          <CategoryList
            categories={event.categoriesAllowed}
            onlyMacro
            macroIconClass="w-[18px] h-[18px]"
            containerClass="flex flex-row flex-nowrap items-center justify-end gap-[3px] overflow-hidden"
          />
        </div>
      </div>

      {/* Body */}
      <div className="px-2.5 pt-1.5 pb-2.5 flex flex-col gap-2 flex-1">
        <div className="min-w-0">
          <h3 className="font-extrabold text-[14px] text-ink-strong leading-tight line-clamp-2 break-words tracking-tight">
            {event.name}
          </h3>
          {locationLabel && (
            <p className="mt-0.5 text-[11.5px] text-gray-500 truncate">
              {locationLabel}
            </p>
          )}
          <div className="mt-0.5">
            <ListingStartsIn event={event} />
          </div>
        </div>

        {showFooter && (
          <div className="mt-auto pt-2 border-t border-line/70 flex flex-col gap-1.5">
            {!hideFillBar && (
              <ListingFillBar
                current={event.bookedSpaces}
                total={event.totalSpaces}
                compact
              />
            )}
            {!hidePrice && (
              <p className="m-0 text-[12px] text-gray-600">
                {t('common.from_price')}{' '}
                <span className="text-[14px] font-extrabold text-ink-strong tabular-nums">
                  {event.lowestPrice}€
                </span>
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const EventCardWrapper = ({
  event,
  hasNavigation,
  small,
  isActive,
  onManageClick,
  onDuplicateClick,
  isUpcoming,
  isAuthenticated,
  isDashboard = false,
  isMap = false,
  isCarousel = false,
  isCompact = false,
  noLabel,
  hideDaysPill = false,
  smallLogo = false,
  equalTitleHeight = false,
  // Events listing on phones: render the two-up compact card instead.
  compactListing = false,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { showAlert, confirm } = useAlert();
  const [isCancelling, setIsCancelling] = useState(false);

  const cardBaseWidth = 420;
  const cardBaseHeight = isCompact ? 160 : 380;
  const cardAspect = isCompact ? 'aspect-[420/160]' : 'aspect-[420/380]';

  const statusStyles = {
    Approved: 'bg-green-500 text-white',
    Pending: 'bg-yellow-500 text-white',
    Rejected: 'bg-red-500 text-white',
    Paid: 'bg-white text-gray-700 border border-gray-300',
    Expired: 'bg-gray-300 text-gray-700',
  };

  const handleNavigateToEventDetail = () => {
    navigate(`/event/${event.slug}`);
  };

  const handleCancelBooking = async (e) => {
    e?.stopPropagation?.();
    const booking = event?.booking;
    if (!booking?.id || isCancelling) return;
    const ok = await confirm({
      type: 'confirm',
      title:
        t('dashboard.cancel_booking_title') || 'Cancel this booking request?',
      message:
        t('dashboard.cancel_booking_message') ||
        "This will release the space and notify the promoter. You won't be able to undo this.",
      confirmLabel: t('dashboard.cancel_booking_confirm') || 'Yes, cancel',
      confirmIntent: 'danger',
      cancelLabel: t('common.back') || 'Back',
    });
    if (!ok) return;

    setIsCancelling(true);
    try {
      await dispatch(cancelBooking(booking.id));
      try {
        dispatch(getBrandEvents());
      } catch {}
      showAlert({
        type: 'success',
        title:
          t('dashboard.cancel_booking_success_title') || 'Booking cancelled',
        message:
          t('dashboard.cancel_booking_success_message') ||
          'Your booking request has been cancelled.',
        primaryAction: { label: t('common.ok') || 'OK' },
      });
    } catch (err) {
      const data = err?.response?.data || {};
      showAlert({
        type: 'error',
        title:
          t('dashboard.cancel_booking_error_title') ||
          'Could not cancel booking',
        message:
          data.message ||
          err?.message ||
          t('dashboard.cancel_booking_error_message') ||
          'Something went wrong while cancelling this booking. Please try again.',
        primaryAction: { label: t('common.ok') || 'OK' },
      });
    } finally {
      setIsCancelling(false);
    }
  };

  const trimOrEmpty = (v) => (typeof v === 'string' ? v.trim() : v || '');
  const getCardLocationLabel = (ev) => {
    const city = trimOrEmpty(ev?.city);
    const country = trimOrEmpty(ev?.country) || trimOrEmpty(ev?.countryCode);

    if (city && country) return `${city}, ${country}`;
    if (city) return city;
    if (country) return country;

    // fallback to legacy field if new ones aren't present
    return trimOrEmpty(ev?.location);
  };

  const stripDots = (s) => (typeof s === 'string' ? s.replaceAll('.', '') : s);

  const fmtMMMYYYY = (d, locale) =>
    moment(d).isValid()
      ? stripDots(moment(d).locale(locale).format('MMM YYYY')).toUpperCase()
      : '';

  const getDateDisplay = (date, locale) => {
    const range = getEventDateRange(date);
    if (!range) return null;

    const mStart = moment(range.startDate);
    const mEnd = moment(range.endDate);

    const sameMonthYear =
      mStart.year() === mEnd.year() && mStart.month() === mEnd.month();
    const startLbl = fmtMMMYYYY(mStart, locale);
    const endLbl = fmtMMMYYYY(mEnd, locale);
    const separator = date?.type === 'fixed' ? '  –  ' : ' – ';
    const label = sameMonthYear ? startLbl : `${startLbl}${separator}${endLbl}`;

    let daysCount;
    if (date?.type === 'segmented' && Array.isArray(date.days)) {
      daysCount = date.days.filter((d) => moment(d?.date).isValid()).length;
    } else {
      daysCount = Math.max(mEnd.diff(mStart, 'days') + 1, 1);
    }

    return { label, daysCount };
  };

  const DateChips = ({ date: rawDate }) => {
    const { i18n, t } = useTranslation();
    const date = normalizeEventDate(rawDate);
    const info = getDateDisplay(date, i18n?.language || 'en');
    if (!info) return null;

    return (
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full bg-gray-600 text-white text-[11px] font-bold tracking-wide">
          {info.label}
        </span>

        {Number.isFinite(info.daysCount) && info.daysCount > 0 && (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full bg-gray-100 text-gray-500 text-[11px] italic font-semibold">
            {info.daysCount}{' '}
            {info.daysCount === 1 ? t('common.day') : t('common.days')}
          </span>
        )}
      </div>
    );
  };

  const statusLabel = event?.booking?.status || '';

  // Booking helpers
  const isBooking = !!event?.booking;
  const booking = event?.booking || null;

  const findBookedSpace = (event, booking) => {
    const placement = booking?.placement || booking?.Placement || null;

    let space = booking?.space;
    const allSpaces = event?.spaces || event?.Spaces || [];
    if (!space && booking?.spaceId && allSpaces.length) {
      space =
        allSpaces.find((s) => String(s.id) === String(booking.spaceId)) || null;
    }
    if (!space && placement?.spaceId && allSpaces.length) {
      space =
        allSpaces.find((s) => String(s.id) === String(placement.spaceId)) ||
        null;
    }

    return space;
  };

  // Booking space description helper (not used currently)
  // const spaceDescription = (space, booking) => {
  //   const raw =
  //     space?.shortDescription ||
  //     space?.description ||
  //     booking?.spaceDescription ||
  //     '';
  //   // very light HTML strip if any
  //   return String(raw).replace(/<[^>]*>/g, '');
  // };

  const formatspacePrice = (p) => {
    if (!p) return null;
    const num = Number(p);
    if (Number.isNaN(num)) return null;
    return `${num.toFixed(2)}€`;
  };

  const formatSizeStr = (s) => {
    if (!s) return null;
    const str = String(s).trim();
    return /m²?$/i.test(str) ? str : `${str} m²`;
  };

  const BookingSummary = ({ compact = false }) => {
    const space = findBookedSpace(event, booking);
    const name = space?.name || 'Booked space';
    const size = formatSizeStr(space?.size);
    // description if needed in the future
    // const desc = spaceDescription(space, booking);
    const price = formatspacePrice(space?.price);

    return (
      <div
        className={
          'flex flex-col h-1/2 items-center justify-center gap-4 lg:px-8 sm:px-2 md:px-4'
        }
      >
        <div className="flex flex-row justify-center items-center w-full gap-6">
          <div className="flex flex-row gap-2 text-lg">
            <p className="text-yellow-05">{t('common.name')}</p>
            <p>{name}</p>
          </div>

          {size && (
            <div className="flex flex-row gap-2 text-lg">
              <p className="text-yellow-05">{t('common.size')}</p>
              <p>{size}</p>
            </div>
          )}
        </div>

        {price && (
          <div className="flex flex-row gap-2 text-lg">
            <p className="text-yellow-05">{t('common.price')}:</p>
            <p>{price}</p>
          </div>
        )}
      </div>
    );
  };

  if (!event) return null;

  // Cancelled events keep their card (with the Cancelled tag) but drop every
  // availability affordance — there is nothing left to book (T2B-420).
  const cancelled = isEventCancelled(event);
  // Past events drop it for the same reason: the bar would describe a
  // booking window that is already closed (T2B-397).
  const hideAvailability =
    cancelled || getEventStatus(event?.date) === 'finished';

  const isListingMode =
    !isMap && !isDashboard && !isCompact && !small && !isBooking;

  const renderCardInner = () => {
    if (isListingMode && compactListing) {
      return (
        <ListingCompactCard event={event} isAuthenticated={isAuthenticated} />
      );
    }
    if (isListingMode) {
      return (
        <ListingEventCard
          event={event}
          isAuthenticated={isAuthenticated}
          hideDaysPill={hideDaysPill}
          smallLogo={smallLogo}
          equalTitleHeight={equalTitleHeight}
        />
      );
    }
    if (isMap) {
      return (
        <div
          className={`relative rounded-3xl bg-white shadow-md transition-all duration-300 ${
            isActive ? 'shadow-xl' : 'hover:shadow-lg'
          } w-full`}
        >
          {event.isDummy && <DummyRibbon />}
          <ScaleBox
            baseWidth={344}
            baseHeight={220}
            ratioClass="aspect-[344/220]"
          >
            <div className="p-2 sm:p-3 h-full flex flex-col">
              <div className="flex flex-row gap-2 sm:gap-3 items-stretch flex-1 min-h-0">
                {/* Image - Left Side */}
                <div className="relative flex-shrink-0 w-[96px] sm:w-[112px] md:w-[128px]">
                  <EventStatusBadge event={event} />
                  <div className="relative w-full aspect-square rounded-2xl border border-gray-200 overflow-hidden">
                    {event?.avatar ? (
                      <img
                        src={event.avatar}
                        alt={event.name || 'Event'}
                        className="absolute inset-0 w-full h-full object-cover"
                        loading="lazy"
                        decoding="async"
                      />
                    ) : (
                      <div className="absolute inset-0 grid place-items-center">
                        <BuildingStorefrontIcon className="w-16 h-16 text-gray-200" />
                      </div>
                    )}
                  </div>
                </div>

                {/* Right Side Column */}
                <div className="flex w-full flex-col min-w-0 h-full">
                  {/* Header: Title, Location, Date */}
                  <div className="flex-shrink-0">
                    <h3
                      title={event.name}
                      className="font-bold text-sm sm:text-base text-gray-800 leading-snug w-full line-clamp-2"
                    >
                      {event.name}
                    </h3>

                    {getCardLocationLabel(event) && (
                      <p
                        title={getCardLocationLabel(event)}
                        className="text-gray-500 text-[10px] sm:text-xs truncate max-w-full mt-0.5"
                      >
                        {getCardLocationLabel(event)}
                      </p>
                    )}

                    <div className="scale-90 origin-left -ml-1 mt-0.5">
                      <DateChips date={event.date} />
                    </div>
                  </div>

                  {/* Body: Categories (Centered like standard card) */}
                  <div className="flex-1 flex flex-col justify-center items-center gap-0.5 min-h-0 overflow-hidden py-0.5">
                    <CategoryList
                      categories={event.categoriesAllowed}
                      onlyMacro
                      withLabel
                      tooltipPlacement="top"
                      containerClass="flex w-full flex-wrap items-center justify-center gap-x-1.5 gap-y-0.5"
                      macroIconClass="w-4 h-4 sm:w-5 sm:h-5"
                    />
                  </div>
                </div>
              </div>

              {/* Footer: Price/Progress (full card width) */}
              {isAuthenticated && (
                <div className="mt-auto flex justify-between items-center gap-2">
                  {hideAvailability ? (
                    <div className="flex-1" />
                  ) : (
                    <ProgressBar
                      current={event.bookedSpaces}
                      total={event.totalSpaces}
                      align="center"
                    />
                  )}

                  <div className="flex flex-col min-w-[70px] items-center justify-center px-2 py-0.5 mr-1 rounded-2xl bg-yellow-03 border border-yellow-100">
                    <p className="text-gray-500 text-[9px] leading-tight">
                      {t('common.from_price')}
                    </p>
                    <p className="text-gray-700 text-sm font-bold leading-tight">
                      {event.lowestPrice}€
                    </p>
                  </div>
                </div>
              )}
            </div>
          </ScaleBox>
        </div>
      );
    }

    return (
      <div className="relative w-full overflow-visible ">
        {/* Status ribbon (NOT inside the overflow-hidden card) */}
        {statusLabel && isDashboard && isCompact && (
          <div
            className={`absolute -top-2 -left-2 z-50 px-3 py-1 rounded-xl text-xs font-semibold capitalize shadow ${statusStyles[statusLabel]}`}
          >
            {statusLabel}
          </div>
        )}
        <div
          className={`relative bg-white flex flex-col rounded-2xl sm:rounded-3xl shadow-md transition-all duration-300 ${
            isActive ? 'ring-2 ring-gray-400 shadow-xl' : 'hover:shadow-lg'
          } w-full`}
        >
          {event.isDummy && <DummyRibbon />}

          {/* Kebab menu — outside ScaleBox so contain:paint doesn't clip the dropdown */}
          {small && isDashboard && (
            <Menu as="div" className="absolute top-3 right-3 z-50">
              <MenuButton aria-label="Open card actions">
                <EllipsisHorizontalIcon className="h-5 w-5 text-gray-600 hover:text-gray-800" />
              </MenuButton>
              <Transition
                as={Fragment}
                enter="transition ease-out duration-200"
                enterFrom="transform opacity-0 scale-95"
                enterTo="transform opacity-100 scale-100"
                leave="transition ease-in duration-150"
                leaveFrom="transform opacity-100 scale-100"
                leaveTo="transform opacity-0 scale-95"
              >
                <MenuItems className="absolute right-0 mt-2 w-40 origin-top-right bg-white divide-y divide-gray-200 rounded-md shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-50">
                  <MenuItem>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onManageClick && onManageClick(event);
                      }}
                      className="data-[focus]:bg-yellow-100 flex w-full px-4 py-3 items-center text-sm text-gray-700"
                    >
                      <CogIcon className="w-4 h-4 mr-2" />
                      {t('common.manage')}
                    </button>
                  </MenuItem>
                  <MenuItem>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleNavigateToEventDetail();
                      }}
                      className="data-[focus]:bg-yellow-100 flex w-full px-4 py-3 items-center text-sm text-gray-700"
                    >
                      <EyeIcon className="w-4 h-4 mr-2" />
                      {t('common.view')}
                    </button>
                  </MenuItem>
                  {!!event?.booking && canCancelBooking(event.booking) && (
                    <MenuItem>
                      <button
                        onClick={handleCancelBooking}
                        disabled={isCancelling}
                        className="data-[focus]:bg-red-50 flex w-full px-4 py-3 items-center text-sm text-red-600 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <NoSymbolIcon className="w-4 h-4 mr-2" />
                        {isCancelling
                          ? t('common.loading') || 'Loading…'
                          : t('dashboard.cancel_booking') || 'Cancel request'}
                      </button>
                    </MenuItem>
                  )}
                </MenuItems>
              </Transition>
            </Menu>
          )}

          <ScaleBox
            baseWidth={cardBaseWidth}
            baseHeight={cardBaseHeight}
            ratioClass={cardAspect}
          >
            <div className="h-full p-3 sm:p-4 md:p-5 flex flex-col">
              {/* Header row */}
              <div className="flex items-start gap-3 sm:gap-4">
                {/* Image */}
                <div className="relative flex-shrink-0 w-[92px] sm:w-[110px] md:w-[112px]">
                  <EventStatusBadge event={event} />
                  <div className="relative w-full aspect-square rounded-2xl border border-gray-200 overflow-hidden">
                    {event?.avatar ? (
                      <img
                        src={event.avatar}
                        alt={event.name || 'Event'}
                        className="absolute inset-0 w-full h-full object-cover"
                        loading="lazy"
                        decoding="async"
                      />
                    ) : (
                      <div className="absolute inset-0 grid place-items-center">
                        <BuildingStorefrontIcon className="w-16 h-16 text-gray-200" />
                      </div>
                    )}
                  </div>
                </div>

                {/* Main text */}
                <div className="flex items-start pl-1 sm:pl-2 w-full min-w-0">
                  <div className="flex-1 min-w-0">
                    <h3
                      className={
                        'font-bold text-base sm:text-lg md:text-xl text-gray-800 leading-snug w-full line-clamp-2 [display:-webkit-box] [-webkit-line-clamp:2] [-webkit-box-orient:vertical] overflow-hidden'
                      }
                      title={event.name}
                    >
                      {event.name}
                    </h3>

                    {getCardLocationLabel(event) && (
                      <p
                        className="text-gray-500 text-xs sm:text-sm truncate max-w-full mt-1"
                        title={getCardLocationLabel(event)}
                      >
                        {getCardLocationLabel(event)}
                      </p>
                    )}

                    <DateChips date={event.date} />
                  </div>
                </div>
              </div>

              {/* Body */}
              {!isCompact &&
                (isBooking ? (
                  <BookingSummary compact={!!small} />
                ) : (
                  !small && (
                    <>
                      <>
                        <div className="pt-3 flex flex-col items-center">
                          {/* reserve space for 2 rows of icons */}
                          <div
                            className={`w-[280px] min-h-[52px] sm:min-h-[60px] flex justify-center ${event.categoriesAllowed.length > 7 ? 'items-start' : 'items-center'}`}
                          >
                            <CategoryList
                              categories={event.categoriesAllowed}
                              onlyMacro
                              withLabel
                              tooltipPlacement="top"
                              containerClass="flex w-full flex-wrap items-center justify-center gap-x-3 gap-y-1.5"
                            />
                          </div>
                        </div>

                        <div className="px-1 sm:px-2 md:px-4 pt-3">
                          <p className="text-gray-500 text-sm italic leading-snug line-clamp-4 min-h-[5.25rem]">
                            {event.shortDescription}
                          </p>
                        </div>

                        {isAuthenticated && (
                          <div className="mt-auto pt-3 flex justify-between items-center sm:flex-row sm:items-center sm:justify-between gap-3">
                            {hideAvailability ? (
                              <div className="flex-1" />
                            ) : (
                              <ProgressBar
                                current={event.bookedSpaces}
                                total={event.totalSpaces}
                              />
                            )}

                            <div className="flex flex-col w-24 items-center justify-center px-6 py-1 rounded-3xl bg-yellow-03 border border-yellow-100 ">
                              <p className="text-gray-500 text-xs">
                                {t('common.from_price')}
                              </p>
                              <p className="text-gray-700 text-lg sm:text-lg font-bold">
                                {event.lowestPrice}€
                              </p>
                            </div>
                          </div>
                        )}
                      </>
                    </>
                  )
                ))}
            </div>
          </ScaleBox>
        </div>
      </div>
    );
  };

  if (hasNavigation) {
    return (
      <div
        role="button"
        tabIndex={0}
        onClick={handleNavigateToEventDetail}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') handleNavigateToEventDetail();
        }}
        className={`no-underline text-current ${
          isCarousel ? (small ? 'min-w-[240px] sm:min-w-[260px]' : '') : ''
        }`}
      >
        {renderCardInner()}
      </div>
    );
  }
  return renderCardInner();
};

export default EventCardWrapper;
